<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import { IRestaurant } from '@/interfaces/IRestaurant';
  import { useMenuV2 } from '@/composition/use-menu-v2';
  import { menuPagePath } from '@/domain/catalog/menu-links';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  // i18n in script context (script setup has no $t)
  const proxy = getCurrentInstance()!.proxy;

  const restaurantStore = useRestaurantStore();
  const { isMenuV2Active } = useMenuV2();

  /** Categories and items: one «Gestionar menú» tile on the Menú page. */
  const menuActions = computed(() => {
    const stringId = restaurantStore.restaurant?.stringId ?? '';
    if (isMenuV2Active.value)
      return [
        {
          title: String(proxy.$t('menuPage.dashboard.tileTitle')),
          icon: 'mdi-menu',
          color: 'orange',
          to: menuPagePath(stringId, true),
          description: String(proxy.$t('menuPage.dashboard.tileSubtitle')),
        },
      ];
    return [
      {
        title: String(proxy.$t('dashboard.qaManageMenu')),
        icon: 'mdi-menu',
        color: 'orange',
        to: menuPagePath(stringId, false, { category: 'new' }),
        description: String(proxy.$t('dashboard.qaManageMenuDesc')),
      },
      {
        title: String(proxy.$t('dashboard.qaManageItems')),
        icon: 'mdi-silverware',
        color: 'orange',
        to: menuPagePath(stringId, false),
        description: String(proxy.$t('dashboard.qaManageItemsDesc')),
      },
    ];
  });

  // Computed
  const quickActions = computed(() => [
    ...menuActions.value,
    {
      title: String(proxy.$t('dashboard.qaQr')),
      icon: 'mdi-qrcode',
      color: 'green',
      to: `/${restaurantStore.restaurant?.stringId}/qr`,
      description: String(proxy.$t('dashboard.qaQrDesc')),
    },
    {
      title: String(proxy.$t('dashboard.qaDesign')),
      icon: 'mdi-palette',
      color: 'purple',
      to: `/${restaurantStore.restaurant?.stringId}/design`,
      description: String(proxy.$t('dashboard.qaDesignDesc')),
    },
    {
      title: String(proxy.$t('dashboard.qaPos')),
      icon: 'mdi-point-of-sale',
      color: 'blue',
      to: `/${restaurantStore.restaurant?.stringId}/pos`,
      description: String(proxy.$t('dashboard.qaPosDesc')),
    },
    {
      title: String(proxy.$t('dashboard.qaStock')),
      icon: 'mdi-package-variant',
      color: 'teal',
      to: `/${restaurantStore.restaurant?.stringId}/stock/items`,
      description: String(proxy.$t('dashboard.qaStockDesc')),
      minTier: 0,
    },
  ]);

  const availableActions = computed(() =>
    quickActions.value.filter(
      (action) => restaurantStore.effectiveTier >= (action.minTier ?? 0)
    )
  );
</script>

<template>
  <v-card class="quick-actions-card" elevation="0">
    <v-card-title class="card-header">
      <div class="header-content">
        <div class="header-left">
          <v-icon class="header-icon">mdi-lightning-bolt</v-icon>
          <span class="header-title">{{ $t('dashboard.quickActions') }}</span>
        </div>
      </div>
    </v-card-title>

    <v-card-text class="card-content">
      <div class="actions-grid">
        <v-hover
          v-for="action in availableActions"
          :key="action.to"
          v-slot="{ hover }"
        >
          <v-card :to="action.to" class="action-item" outlined>
            <v-card-text class="action-content">
              <v-icon :color="action.color" size="32" class="action-icon">
                {{ action.icon }}
              </v-icon>
              <div class="action-text">
                <h4 class="action-title">{{ action.title }}</h4>
                <p class="action-description">{{ action.description }}</p>
              </div>
              <v-icon color="grey" size="20" class="arrow-icon">
                mdi-chevron-right
              </v-icon>
            </v-card-text>
          </v-card>
        </v-hover>
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped lang="scss">
  .quick-actions-card {
    @apply tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-neutral-100;
    @apply tw-h-full;

    .card-header {
      @apply tw-pb-2 tw-pt-3;

      .header-content {
        @apply tw-flex tw-items-center tw-justify-between tw-w-full;

        .header-left {
          @apply tw-flex tw-items-center tw-gap-2;

          .header-icon {
            @apply tw-text-orange-500;
            font-size: 20px;
          }

          .header-title {
            @apply tw-text-base tw-font-semibold tw-text-neutral-800;
          }
        }
      }
    }

    .card-content {
      @apply tw-pt-0;
    }

    .actions-grid {
      @apply tw-grid tw-grid-cols-1 tw-gap-2;
      @apply xl:tw-grid-cols-2;

      .action-item {
        @apply tw-transition-all tw-duration-200 tw-cursor-pointer tw-rounded-md;
        @apply tw-border-neutral-200;

        &:hover {
          @apply tw-border-neutral-300 tw-bg-neutral-50 tw-shadow-sm;

          .arrow-icon {
            @apply tw-opacity-100;
          }
        }

        .action-content {
          @apply tw-flex tw-items-center tw-space-x-3 tw-py-2 tw-px-3;
        }

        .action-icon {
          @apply tw-flex-shrink-0;
        }

        .action-text {
          @apply tw-flex-grow tw-min-w-0;

          .action-title {
            @apply tw-text-sm tw-font-semibold tw-text-neutral-800;
            @apply tw-truncate;
          }

          .action-description {
            @apply tw-text-xs tw-text-neutral-600 tw-mt-0;
            @apply tw-truncate;
          }
        }

        .arrow-icon {
          @apply tw-flex-shrink-0 tw-opacity-0 tw-transition-opacity;
        }
      }
    }
  }
</style>
